import React from 'react'
import { Grid, generateSrcSet, makeStyles } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import type { QuoteFile } from 'paintscout'
import { ImgComparisonSlider } from '@img-comparison-slider/react'
import type { PresentationBlockProps } from '../..'
import compareBlockProps from '../../util/compareBlockProps'
import { InlineEditableMedia } from '../../inlineEditable'
import { usePresentation } from '../../../context'

// QuoteFile.height is optional — used when either image is missing dimension data so the
// slider doesn't collapse to a zero/NaN height.
const DEFAULT_SLIDER_HEIGHT = 400

const useStyles = makeStyles<Theme, { maxHeight: number }>(
  (_theme) => ({
    root: {},
    slider: ({ maxHeight }) => ({
      justifyContent: 'center',
      width: '100%',
      maxWidth: '100%',
      overflow: 'hidden',
      maxHeight,
      height: maxHeight,
      '&:focus': {
        outline: 'none'
      }
    }),
    image: ({ maxHeight }) => ({
      maxHeight,
      height: maxHeight,
      width: '100%',
      maxWidth: '100%',
      objectFit: 'cover'
    }),
    svg: { margin: '-20px' }
  }),
  { name: 'SliderImageBlock' }
)

function SliderImageBlock(props: PresentationBlockProps) {
  const {
    isEditable,
    isSettings,
    presentationIndustry,
    block,
    blockPrefix,
    setFieldValue,
    submitForm,
    WrapperComponent
  } = props
  const { presentationLibrary } = usePresentation()
  const { fields } = block
  const { leftImage, rightImage } = fields ?? {}

  const handleEditField = (field: string, content: QuoteFile) => {
    setFieldValue(`${blockPrefix}.${field}`, content)
    submitForm()
  }

  return (
    <WrapperComponent {...props}>
      <Grid container spacing={3} style={{ justifyContent: 'center' }}>
        {isEditable && (
          <>
            <Grid item md={6} sm={6} xs={12}>
              <InlineEditableMedia
                mediaOptionsProps={{ blockPrefix, fieldSuffix: 'leftImage' }}
                file={leftImage}
                handleEdit={(file) => handleEditField('fields.leftImage', file)}
                isEditable={isEditable}
                isSettings={isSettings}
                placeholderButtonText={'Add Before Image'}
                presentationIndustry={presentationIndustry}
                presentationLibrary={presentationLibrary}
                youtube={false}
              />
            </Grid>
            <Grid item md={6} sm={6} xs={12}>
              <InlineEditableMedia
                mediaOptionsProps={{ blockPrefix, fieldSuffix: 'rightImage' }}
                file={rightImage}
                handleEdit={(file) => handleEditField('fields.rightImage', file)}
                isEditable={isEditable}
                isSettings={isSettings}
                placeholderButtonText={'Add After Image'}
                presentationIndustry={presentationIndustry}
                presentationLibrary={presentationLibrary}
                youtube={false}
              />
            </Grid>
          </>
        )}
        {leftImage && rightImage && (
          <Grid item md={12} sm={12} xs={12}>
            <SliderImageContainer leftImage={leftImage} rightImage={rightImage} />
          </Grid>
        )}
      </Grid>
    </WrapperComponent>
  )
}

const SliderImageContainer = React.memo(function SliderImageContainer({
  leftImage,
  rightImage
}: {
  leftImage: QuoteFile
  rightImage: QuoteFile
}) {
  const generatedLeftSrc = generateSrcSet({ file: leftImage })
  const generatedRightSrc = generateSrcSet({ file: rightImage })
  const leftHeight = leftImage.height ?? rightImage.height ?? DEFAULT_SLIDER_HEIGHT
  const rightHeight = rightImage.height ?? leftImage.height ?? DEFAULT_SLIDER_HEIGHT
  const maxHeight = Math.min(leftHeight, rightHeight)
  const classes = useStyles({ maxHeight })

  return (
    <ImgComparisonSlider className={classes.slider}>
      <svg
        // @ts-ignore
        slot="handle"
        xmlns="http://www.w3.org/2000/svg"
        width="100"
        viewBox="-8 -3 16 6"
        className={classes.svg}
      >
        <path
          stroke="#fff"
          d="M -5 -2 L -7 0 L -5 2 M -5 -2 L -5 2 M 5 -2 L 7 0 L 5 2 M 5 -2 L 5 2"
          strokeWidth="1"
          fill="#fff"
          vectorEffect="non-scaling-stroke"
        ></path>
      </svg>
      <img
        slot="first"
        src={leftImage.src}
        alt={(leftImage as any).alt}
        srcSet={generatedLeftSrc}
        className={classes.image}
      />
      <img
        slot="second"
        src={rightImage.src}
        alt={(rightImage as any).alt}
        srcSet={generatedRightSrc}
        className={classes.image}
      />
    </ImgComparisonSlider>
  )
})

export default React.memo(SliderImageBlock, (prevProps, nextProps) => {
  return compareBlockProps(prevProps, nextProps)
})
